import { useMemo, useState } from "react";
import { Header } from "@/components/header";
import { Footer } from "@/components/footer";
import {
  Lightbulb, FolderOpen, ShieldCheck, Gavel, Scale, AlertTriangle, UserCheck,
  CheckCircle2, XCircle, PhoneCall, Lightbulb as TipIcon, BookOpen, FileText,
} from "lucide-react";
import { useAwarenessImages, useMustKnowLaws, useLawAwarenessSetting, type AwarenessImage, type MustKnowLaw } from "@/hooks/use-supabase-data";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

// ─── Icon resolver for law cards ────────────────────────────────────────────
const LAW_ICONS: Record<string, React.ElementType> = {
  'shield-check': ShieldCheck,
  'gavel': Gavel,
  'scale': Scale,
  'alert-triangle': AlertTriangle,
  'user-check': UserCheck,
}
function LawIcon({ name }: { name: string }) {
  const Icon = LAW_ICONS[name] ?? ShieldCheck
  return <Icon className="w-5 h-5 text-white" />
}

// ─── Awareness image components ──────────────────────────────────────────────
interface AwarenessImageDisplayProps {
  image: AwarenessImage;
}

const AwarenessImageDisplay = ({ image }: AwarenessImageDisplayProps) => {
  return (
    <div className="w-full">
      <img
        src={image.image_url}
        alt={image.title || "Awareness image"}
        className="w-full h-auto rounded-lg"
      />
      {(image.title || image.description) && (
        <div className="mt-4">
          {image.title && (
            <h3 className="font-poppins text-xl font-bold text-foreground mb-2">
              {image.title}
            </h3>
          )}
          {image.description && (
            <p className="font-inter text-sm text-muted-foreground">
              {image.description}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

interface CategorySectionProps {
  category: string;
  images: AwarenessImage[];
}

function CategorySection({ category, images }: CategorySectionProps) {
  return (
    <section className="mb-12">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 neo-icon-box">
          <FolderOpen className="w-4 h-4 text-white" />
        </div>
        <div>
          <h2 className="font-poppins text-xl md:text-2xl font-bold text-foreground">
            {category}
          </h2>
          <p className="font-inter text-xs md:text-sm text-muted-foreground">
            {images.length} image{images.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <div className="space-y-8">
        {images.map((image) => (
          <AwarenessImageDisplay key={image.id} image={image} />
        ))}
      </div>
    </section>
  );
}

// ─── Law card — click opens a popup dialog ───────────────────────────────────
function LawCard({ law }: { law: MustKnowLaw }) {
  const [open, setOpen] = useState(false)

  return (
    <>
      {/* Card — always compact, click to open popup */}
      <button
        onClick={() => setOpen(true)}
        className="neo-card rounded-2xl p-5 flex items-start gap-3 w-full text-left hover:shadow-md transition-shadow cursor-pointer"
      >
        <div className="p-2.5 rounded-xl bg-gradient-to-br from-rose-500 to-orange-500 neo-icon-box shrink-0 mt-0.5">
          <LawIcon name={law.icon} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-poppins text-base font-bold text-foreground leading-snug">
            {law.title}
          </h3>
          <p className="font-inter text-sm text-muted-foreground mt-1 leading-relaxed line-clamp-2">
            {law.summary}
          </p>
          {law.bns_section && (
            <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-medium">
              <BookOpen className="w-3 h-3" />
              {law.bns_section}
            </span>
          )}
          <p className="text-xs text-primary font-medium mt-3">Tap to view details →</p>
        </div>
      </button>

      {/* Popup dialog with full details */}
      <Dialog open={open} onOpenChange={setOpen}>
        {/* p-0 + overflow-hidden lets us control padding per section */}
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg rounded-2xl p-0 gap-0 overflow-hidden flex flex-col">

          {/* Fixed header — never scrolls away */}
          <div className="shrink-0 px-5 pt-5 pb-4 border-b border-border pr-10">
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-rose-500 to-orange-500 neo-icon-box shrink-0">
                <LawIcon name={law.icon} />
              </div>
              <div>
                <DialogTitle className="font-poppins text-base font-bold leading-snug text-left">
                  {law.title}
                </DialogTitle>
                <p className="font-inter text-sm text-muted-foreground mt-1">{law.summary}</p>
                {law.bns_section && (
                  <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-medium">
                    <BookOpen className="w-3 h-3" />
                    {law.bns_section}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Scrollable body */}
          <div className="overflow-y-auto flex-1 px-5 py-4 flex flex-col gap-5" style={{ maxHeight: '60vh' }}>
            {/* Rights */}
            {law.rights_points && law.rights_points.length > 0 && (
              <div>
                <p className="font-poppins text-xs font-semibold uppercase tracking-wide text-emerald-600 mb-2">
                  {law.rights_title}
                </p>
                <ul className="space-y-2">
                  {law.rights_points.map((pt, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Proof / Documents */}
            {law.proof_documents && law.proof_documents.length > 0 && (
              <div>
                <p className="font-poppins text-xs font-semibold uppercase tracking-wide text-violet-600 mb-2">
                  Proof / Documents to Gather
                </p>
                <ul className="space-y-2">
                  {law.proof_documents.map((doc, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                      <FileText className="w-4 h-4 text-violet-500 shrink-0 mt-0.5" />
                      <span>{doc}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Consequences */}
            {law.punishment_points && law.punishment_points.length > 0 && (
              <div>
                <p className="font-poppins text-xs font-semibold uppercase tracking-wide text-rose-600 mb-2">
                  {law.punishment_title}
                </p>
                <ul className="space-y-2">
                  {law.punishment_points.map((pt, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                      <XCircle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Where to complain */}
            {law.support_points && law.support_points.length > 0 && (
              <div>
                <p className="font-poppins text-xs font-semibold uppercase tracking-wide text-blue-600 mb-2">
                  {law.support_title}
                </p>
                <ul className="space-y-2">
                  {law.support_points.map((pt, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                      <PhoneCall className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Tip */}
            {law.tip && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <TipIcon className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                <p className="font-inter text-xs text-amber-800">{law.tip}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Law Awareness section ───────────────────────────────────────────────────
function LawAwarenessSection() {
  const { laws, loading } = useMustKnowLaws()
  const { isEnabled, loading: settingLoading } = useLawAwarenessSetting()

  if (settingLoading || loading) {
    return (
      <section className="mb-12">
        <div className="flex items-center gap-3 mb-6">
          <Skeleton className="w-10 h-10 rounded-xl" />
          <Skeleton className="w-48 h-7 rounded" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-64 rounded-2xl" />)}
        </div>
      </section>
    )
  }

  if (!isEnabled || !laws || laws.length === 0) return null

  const orderedLaws = laws.slice().sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999))

  return (
    <section className="mb-12">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-xl bg-gradient-to-br from-rose-500 to-orange-500 neo-icon-box">
          <ShieldCheck className="w-4 h-4 text-white" />
        </div>
        <div>
          <h2 className="font-poppins text-xl md:text-2xl font-bold text-foreground">
            Law Awareness
          </h2>
          <p className="font-inter text-xs md:text-sm text-muted-foreground">
            Know your legal rights and where to seek help
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {orderedLaws.map((law) => (
          <LawCard key={law.id} law={law} />
        ))}
      </div>
    </section>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function Awareness() {
  const { images, loading } = useAwarenessImages();

  const imagesByCategory = useMemo(() => {
    if (!images) return {};

    const grouped: Record<string, AwarenessImage[]> = {};
    images.forEach((image) => {
      const category = image.category || 'Uncategorized';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(image);
    });

    const sortedCategoryNames = Object.keys(grouped).sort((a, b) => {
      if (a === 'Uncategorized') return 1;
      if (b === 'Uncategorized') return -1;
      return a.localeCompare(b);
    });

    const sortedGroupedImages: Record<string, AwarenessImage[]> = {};
    sortedCategoryNames.forEach(cat => {
      sortedGroupedImages[cat] = grouped[cat];
    });

    return sortedGroupedImages;
  }, [images]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-slate-600">Loading awareness content...</p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categories = Object.keys(imagesByCategory);
  const hasImages = images && images.length > 0;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <div className="text-center">
            <h1 className="font-poppins text-2xl md:text-3xl font-bold text-foreground mb-2">
              Awareness Campaigns
            </h1>
            <p className="font-inter text-muted-foreground text-sm md:text-base">
              Stay informed and empowered with our community awareness initiatives.
            </p>
          </div>
        </div>

        {/* Awareness images by category */}
        {hasImages ? (
          categories.map((category) => (
            <CategorySection
              key={category}
              category={category}
              images={imagesByCategory[category]}
            />
          ))
        ) : (
          <div className="text-center py-16">
            <Lightbulb className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="font-inter text-muted-foreground text-lg">
              No awareness images added yet.
            </p>
          </div>
        )}

        {/* Law Awareness section */}
        <LawAwarenessSection />
      </main>
      <Footer />
    </div>
  );
}
